import React from "react";
import EmailRoundedIcon from "@material-ui/icons/EmailRounded";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import GitHubIcon from "@material-ui/icons/GitHub";

const SECTIONS = [
    { id: "intro", label: "Home" },
    { id: "about", label: "About" },
    { id: "experience", label: "Experience" },
    { id: "projects", label: "Projects" },
    { id: "certifications", label: "Certifications" },
    { id: "events", label: "Events" },
];

const SOCIALS = [
    {
        href: "mailto:hansagayashan7@gmail.com",
        label: "Email",
        icon: <EmailRoundedIcon style={{ fontSize: 20 }} />,
    },
    {
        href: "https://github.com/GayashanHansaja",
        label: "GitHub",
        icon: <GitHubIcon style={{ fontSize: 19 }} />,
    },
    {
        href: "https://linkedin.com/in/gayashan-hansaja",
        label: "LinkedIn",
        icon: <LinkedInIcon style={{ fontSize: 20 }} />,
    },
    {
        href: "https://medium.com/@GayashanHansaja",
        label: "Medium",
        icon: (
            <img
                src="/assets/medium.svg"
                alt=""
                aria-hidden="true"
                className="h-[18px] w-[18px] opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            />
        ),
    },
    {
        href: "https://leetcode.com/u/Gayashan_H/",
        label: "LeetCode",
        icon: (
            <img
                src="/assets/leetcode.png"
                alt=""
                aria-hidden="true"
                className="h-[18px] w-[18px] opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            />
        ),
    },
];

function useActiveSection() {
    const [active, setActive] = React.useState(SECTIONS[0].id);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                const visible = entries
                    .filter(entry => entry.isIntersecting)
                    .sort(
                        (a, b) => b.intersectionRatio - a.intersectionRatio
                    )[0];
                if (visible) setActive(visible.target.id);
            },
            { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 1] }
        );

        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    return active;
}

function scrollToSection(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
}

const SocialLinks = ({ className = "" }) => (
    <div className={className}>
        {SOCIALS.map(social => (
            <a
                key={social.label}
                href={social.href}
                aria-label={social.label}
                target={
                    social.href.startsWith("mailto:") ? undefined : "_blank"
                }
                rel="noopener noreferrer"
                className="group flex h-9 w-9 shrink-0 items-center justify-center
                           rounded-full text-ink-faint transition-all duration-300
                           ease-soft hover:text-accent md:hover:-translate-y-0.5"
            >
                {social.icon}
            </a>
        ))}
    </div>
);

export default function NavBar() {
    const active = useActiveSection();

    return (
        <>
            {/* Desktop rail */}
            <aside
                className="fixed inset-y-0 left-0 z-50 hidden w-[104px] flex-col
                           items-center justify-between border-r border-line
                           bg-canvas-deep py-8 md:flex"
            >
                <button
                    type="button"
                    onClick={() => scrollToSection("intro")}
                    aria-label="Back to top"
                    className="transition-transform duration-300 ease-soft hover:-translate-y-0.5"
                >
                    <img
                        src="/assets/my_logo.png"
                        alt="Gayashan Hansaja"
                        className="h-14 w-auto"
                    />
                </button>

                <nav aria-label="Sections" className="flex flex-col gap-5">
                    {SECTIONS.map(({ id, label }) => {
                        const isActive = active === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => scrollToSection(id)}
                                aria-label={label}
                                aria-current={isActive ? "true" : undefined}
                                className="group relative flex h-4 items-center justify-center"
                            >
                                <span
                                    className={`h-px transition-all duration-300 ease-soft ${
                                        isActive
                                            ? "w-8 bg-accent"
                                            : "w-4 bg-ink-faint group-hover:w-7 group-hover:bg-ink-dim"
                                    }`}
                                />
                                <span
                                    className="pointer-events-none absolute left-full ml-4 whitespace-nowrap
                                               rounded border border-line bg-canvas-deep px-2.5 py-1
                                               font-mono text-[10px] uppercase tracking-[0.14em] text-ink
                                               opacity-0 shadow-lift transition-opacity duration-200
                                               group-hover:opacity-100"
                                >
                                    {label}
                                </span>
                            </button>
                        );
                    })}
                </nav>

                <SocialLinks className="flex flex-col items-center gap-1" />
            </aside>

            {/* Mobile bar. Five 36px icons plus the wordmark overflow a 360px
                viewport, so the wordmark only appears once there's room. */}
            <header
                className="fixed inset-x-0 top-0 z-50 flex h-14 items-center justify-between
                           gap-2 border-b border-line bg-canvas-deep/90 px-4 backdrop-blur-md
                           md:hidden"
            >
                <button
                    type="button"
                    onClick={() => scrollToSection("intro")}
                    className="flex shrink-0 items-center gap-3"
                    aria-label="Back to top"
                >
                    <img
                        src="/assets/my_logo.png"
                        alt=""
                        aria-hidden="true"
                        className="h-8 w-auto"
                    />
                    <span className="hidden font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim min-[420px]:inline">
                        Gayashan Hansaja
                    </span>
                </button>
                <SocialLinks className="flex items-center" />
            </header>
        </>
    );
}
